/**
 * Intercepts edit submit when the primary contact is dirty and drives {@link ContactChangeModal}.
 */

import * as React from 'react';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { getErrorMessage } from '@/utils/errors-utils';
import { buildPhoneSetupLoginPath } from '@/utils/user-contact-utils';
import type {
  ContactChangeKind,
  ContactChangePhase,
} from '@/components/common/modals/ContactChangeModal';

/** Inputs for {@link useContactChangeOnSubmit}. */
export interface UseContactChangeOnSubmitOptions {
  isDirty: boolean;
  kind: 'initial' | 'email' | 'phone';
  ownProfile: boolean;
  closeForm: () => void;
  /** Live primary phone (E.164) — self-change fallback when the PATCH omitted the link. */
  fallbackPhone?: string;
}

/** PATCH outcome after the domain mutation settles. */
export type ContactChangeMutationSettled = {
  ok: boolean;
  link?: string;
};

/** Public API of {@link useContactChangeOnSubmit}. */
export interface UseContactChangeOnSubmitResult {
  isIntercepting: boolean;
  requestSubmit: (run: () => void) => void;
  confirm: () => void;
  cancel: () => void;
  onMutationSettled: (result: ContactChangeMutationSettled) => void;
  onCopyLink: () => void;
  modal: {
    open: boolean;
    kind: ContactChangeKind;
    ownProfile: boolean;
    phase: ContactChangePhase;
    phoneSetupLink?: string;
  };
}

/** Delay before logout after a successful own-profile email/phone change. */
const OWN_CHANGE_LOGOUT_MS = 1_500;
/** Delay after copying a phone-setup link before closing the form. */
const PHONE_COPY_CLOSE_MS = 1_200;

/** Modal kind when the live primary is email or phone. */
function toModalKind(kind: 'initial' | 'email' | 'phone'): ContactChangeKind {
  return kind === 'phone' ? 'phone' : 'email';
}

/** Submit helper: modal first when primary changed, then one domain PATCH. */
export function useContactChangeOnSubmit(
  options: UseContactChangeOnSubmitOptions,
): UseContactChangeOnSubmitResult {
  const { isDirty, kind, ownProfile, closeForm, fallbackPhone } = options;
  const { logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [phase, setPhase] = React.useState<ContactChangePhase>('confirm');
  const [phoneSetupLink, setPhoneSetupLink] = React.useState<string | undefined>();
  const pendingRunRef = React.useRef<(() => void) | null>(null);
  const closeTimerRef = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    return () => {
      if (closeTimerRef.current !== undefined) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const isIntercepting = open;

  function requestSubmit(run: () => void): void {
    if (!isDirty || kind === 'initial') {
      run();
      return;
    }
    pendingRunRef.current = run;
    setPhoneSetupLink(undefined);
    setPhase('confirm');
    setOpen(true);
  }

  function confirm(): void {
    setPhase('pending');
    pendingRunRef.current?.();
  }

  function cancel(): void {
    if (phase === 'pending' || phase === 'copy') return;
    pendingRunRef.current = null;
    setOpen(false);
    setPhase('confirm');
  }

  function scheduleOwnLogout(redirectUrl: string): void {
    closeTimerRef.current = window.setTimeout(() => {
      void logout().then(() => {
        window.location.assign(redirectUrl);
      });
    }, OWN_CHANGE_LOGOUT_MS);
  }

  function resolveOwnPhoneRedirect(link?: string): string {
    if (link && link.length > 0) return link;
    const phone = fallbackPhone?.trim();
    if (phone) return buildPhoneSetupLoginPath(phone);
    return '/login';
  }

  function onMutationSettled(result: ContactChangeMutationSettled): void {
    if (!result.ok) {
      setPhase('confirm');
      return;
    }
    const link = result.link;
    if (ownProfile && kind === 'phone') {
      toast.success('Téléphone mis à jour. Vérifiez le code SMS sur le prochain écran…');
      scheduleOwnLogout(resolveOwnPhoneRedirect(link));
      return;
    }
    if (ownProfile && kind === 'email') {
      toast.success('Email mis à jour. Consultez la nouvelle boîte — déconnexion…');
      scheduleOwnLogout('/login');
      return;
    }
    if (kind === 'phone') {
      if (!link) {
        toast.error(getErrorMessage('phoneSetupLinkMissing'));
        setOpen(false);
        closeForm();
        return;
      }
      setPhoneSetupLink(link);
      setPhase('copy');
      return;
    }
    toast.success('Email mis à jour. Un lien d’activation a été envoyé à la nouvelle adresse.');
    setOpen(false);
    closeForm();
  }

  function onCopyLink(): void {
    if (!phoneSetupLink) return;
    void navigator.clipboard
      .writeText(phoneSetupLink)
      .then(() => {
        toast.success('Lien de configuration téléphone copié.');
        closeTimerRef.current = window.setTimeout(() => {
          setOpen(false);
          closeForm();
        }, PHONE_COPY_CLOSE_MS);
      })
      .catch(() => {
        toast.error(getErrorMessage('clipboardCopyFailed'));
      });
  }

  return {
    isIntercepting,
    requestSubmit,
    confirm,
    cancel,
    onMutationSettled,
    onCopyLink,
    modal: {
      open,
      kind: toModalKind(kind),
      ownProfile,
      phase,
      phoneSetupLink,
    },
  };
}
